import type { NetlifyCountry } from '#types/locationSelector'

type Language = 'EN' | 'ES'

export interface LanguagePreferencePlugin {
  set: (language: Language) => void
  get: () => string | null | undefined
  browserLanguage: string | null
}

declare module '#app' {
  interface NuxtApp {
    $languagePref: LanguagePreferencePlugin
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $languagePref: LanguagePreferencePlugin
  }
}

export default defineNuxtPlugin<{ languagePref: LanguagePreferencePlugin }>(async () => {
  const { enableLanguagePreference } = useFeatureFlags()
  const [lang] = navigator.language.split('-')

  if (!enableLanguagePreference) {
    return {
      provide: {
        languagePref: {
          set: () => null,
          get: () => null,
          browserLanguage: null
        }
      }
    }
  }

  const { DialogLanguagePreference } = useDialogsStore()
  const route = useRoute()
  const router = useRouter()

  const languagePreference = useCookie('languagePreference', {
    maxAge: 2592000 // 30 days
  })

  /**
   * Add/remove -es suffix in the help page routes to redirect to English/Spanish content
   * For example:
   * /en-us/help/service/mobile to /en-us/help-es/services-es/mobile-es
   * /en-us/order-status to /en-us/order-status-es
   */
  const getTransformedUrl = (language: Language) => {
    if (language === 'ES')
      return route.path.split('/en-us')[1].replace(/[^/]+/g, (m) => `${m}-es`)
    else
      return route.path.replaceAll('-es', '')
  }

  const set = (language: Language) => {
    const currentQuery = Object.fromEntries((new URLSearchParams(window.location.search)).entries())

    languagePreference.value = language
    refreshCookie('languagePreference')

    router.replace({
      path: getTransformedUrl(language),
      query: currentQuery
    })
  }

  watch(() => route.path, async () => {
    if (languagePreference.value) {
      return
    }

    const [userCountry]: [NetlifyCountry['country']] = await Promise.all([
      $fetch<NetlifyCountry>('/get-user-geo-country').then((data) => data.country)
    ])

    const appCountryCode = useCountryCode()
    const userCountryCode = userCountry?.code?.toLowerCase()
    const translationsAvailable = ['gift-card', 'help', 'order-status', 'xplr-pass']
    const excludedPages = ['privacy']
    const hasSpanishPage = route.path.split('/').some((page) => translationsAvailable.includes(page))
      && !route.path.split('/').some((page) => excludedPages.includes(page))

    if ((lang.toLowerCase() === 'es' || userCountryCode === 'es') && appCountryCode === 'US' && hasSpanishPage) {
      DialogLanguagePreference.open()
    }
  }, { immediate: true })

  return {
    provide: {
      languagePref: {
        set,
        get: () => languagePreference.value,
        browserLanguage: lang
      }
    }
  }
})
